import Joyride from "react-joyride";
import Icon from "../Icon/Icon.jsx";
import styles from "./popOver.css.js";

const joyrideLocales = {
  // What to display for the different interactive elements.
  back: (
    <div aria-label="zurück">
      <Icon name="chevron" size="24px" />
    </div>
  ),
  close: "Schließen", // Alt. text for the close button.
  last: "Jetzt loslegen!",
  next: (
    <div aria-label="weiter">
      <Icon name="chevron" size="24px" />
    </div>
  ),
  open: `Information anzeigen`, // Alt. text for the beacon.
  skip: "Tour beenden",
};

const joyrideStyles = {
  tooltip: styles.tooltip,
  tooltipContainer: styles.tooltipContainer,
  tooltipTitle: styles.tooltipTitle,
  tooltipContent: styles.tooltipContent,
  tooltipFooter: styles.tooltipFooter,
  buttonSkip: styles.buttonSkip,
  buttonBack: styles.buttonBack,
  buttonNext: styles.buttonNext,
  buttonClose: styles.buttonClose,
  spotlight: styles.spotlight,
};

const joyrideFloaterProps = {
  //Overwriting styles set by floater, a library used by Joyride. See https://github.com/gilbarbara/react-floater/blob/main/src/modules/styles.ts.
  styles: {
    floater: styles.shadow,
  },
};

/**
 * PopOver
 * Decorates the Joyride Library Component (https://docs.react-joyride.com/)
 * Control via state can be used by with callback, run and stepIndex.
 *
 * This component can be extended for elements that work as overlays, connected to an element on the page.
 * If you need a non-specific, more present dialog, take a look at the Dialog component.
 *
 * @param {Object[]} steps - An array of step definitions for the guided tour. Each step should define its target, content, and styling.
 * @param {boolean} [disableOverlay=false] - If true, disables the dimmed overlay over the rest of the screen during the tour.
 * @param {Object} [customStyles] - Custom styles to apply over the default `react-joyride` styles.
 * @param {boolean} [hideCloseButton=false] - If true, hides the close button for the entire tour. Can be overridden by individual step settings.
 * @param {Object} [floaterProps=joyrideFloaterProps] - Properties to configure the behavior and appearance of the tooltips (floater) used by the tour.
 * @param {function} callback - A function invoked when certain events occur during the tour, such as step changes or completion.
 * @param {boolean} run - A boolean indicating whether the tour should be running or paused.
 * @param {number} stepIndex - Which is the current step index
 * @param {boolean} [disableOverlayClose=false]
 * @returns {JSX.Element} A React component rendering the tour using `react-joyride`.
 */
export const PopOver = ({
  steps,
  disableOverlay = false,
  disableOverlayClose = false,
  hideCloseButton = false,
  customStyles,
  floaterProps = joyrideFloaterProps,
  callback,
  run,
  stepIndex,
}) => {
  // Do not show the backButton on the last step of the tour
  // And style the nextButton (which finishes the tour in this case) differently
  const tourSteps = steps.map((step, index) =>
    index === steps.length - 1
      ? {
          ...step,
          hideBackButton: true,
          styles: { buttonNext: styles.buttonOnly },
        }
      : step,
  );

  return (
    <Joyride
      continuous={true} // The provided steps belong to one tour.
      showSkipButton={steps.length > 1} // User can only skip the tour if there's more than 1 step.
      hideCloseButton={hideCloseButton} // Show no close button; can be overwritten by individual steps.
      disableOverlay={disableOverlay}
      disableOverlayClose={disableOverlayClose}
      locale={joyrideLocales}
      steps={tourSteps}
      styles={{
        // Overwriting styles set by Joyride. See https://docs.react-joyride.com/styling and https://github.com/gilbarbara/react-joyride/blob/main/src/styles.ts.
        ...joyrideStyles,
        ...customStyles,
      }}
      floaterProps={floaterProps}
      run={run}
      stepIndex={stepIndex}
      callback={callback}
    />
  );
};
